/**
 * SEO strings per language. Used by server.js to inject meta tags.
 * Add a new language by adding an entry to SUPPORTED_LANGS and STRINGS.
 * Per-page overrides in PAGE_STRINGS; paths in NOINDEX_PAGE_KEYS get meta robots noindex,nofollow.
 */
const DEFAULT_LANG = 'fr';

const SUPPORTED_LANGS = ['fr', 'en', 'es', 'de', 'pt'];

/** First path segments that should receive noindex,nofollow.
 *  Matched against the FIRST segment of the path rest, so e.g. 'chat' covers
 *  /chat/:conversationId and 's' covers /s/:slug. Use for private pages
 *  (account, library, conversations) and transient flows (share resolver,
 *  claim). Community is intentionally indexable and not listed here. */
const NOINDEX_PAGE_KEYS = ['my-builds', 'profile', 'chat', 's', 'ai'];

/** Path rests whose meta is fetched per-page by server.js (a shared build, a
 *  creator profile) rather than taken from STRINGS/PAGE_STRINGS.
 *  Shared with the client so usePageSeo can leave those titles alone instead of
 *  overwriting the server's build-specific one on mount. */
const BUILD_PATH_RE = /^\/gen\/([^/]+)\/([^/]+)(?:\/[^/]+)?$/;
const CREATOR_PATH_RE = /^\/community\/(?!creators$)([^/]+)$/;

const STRINGS = {
  fr: {
    title: "dofgen - Theorycraft le stuff optimal",
    description: "Trouve la meilleure combinaison d'items selon tes critères avec notre créateur de stuffs automatisé.",
    ogLocale: "fr_FR",
    jsonLdAlternateName: "Générateur de stuffs Dofus",
    ogImageAlt: "dofgen - Theorycraft le stuff optimal",
  },
  en: {
    title: "dofgen - Theorycraft the optimal build",
    description: "Find the best combination of items according to your criteria with our automated build creator.",
    ogLocale: "en_US",
    jsonLdAlternateName: "Dofus Build Generator",
    ogImageAlt: "dofgen - Theorycraft the optimal build",
  },
  es: {
    title: "dofgen - Theorycraft la build óptima",
    description: "Encuentra la mejor combinación de objetos según tus criterios con nuestro creador de builds automatizado.",
    ogLocale: "es_ES",
    jsonLdAlternateName: "Generador de builds Dofus",
    ogImageAlt: "dofgen - Theorycraft la build óptima",
  },
  de: {
    title: "dofgen - Theorycraft den optimalen Build",
    description: "Finde mit unserem automatisierten Build-Generator die beste Gegenstandskombination für deine Kriterien.",
    ogLocale: "de_DE",
    jsonLdAlternateName: "Dofus Build-Generator",
    ogImageAlt: "dofgen - Theorycraft den optimalen Build",
  },
  pt: {
    title: "dofgen - Theorycraft a build ideal",
    description: "Encontre a melhor combinação de itens segundo os seus critérios com o nosso criador de builds automatizado.",
    ogLocale: "pt_BR",
    jsonLdAlternateName: "Gerador de builds Dofus",
    ogImageAlt: "dofgen - Theorycraft a build ideal",
  },
};

/** Per-page SEO overrides. Key = path rest without leading slash ('' = home). */
const PAGE_STRINGS = {
  '': {
    fr: { title: "dofgen - Theorycraft le stuff optimal", description: "Trouve la meilleure combinaison d'items selon tes critères avec notre créateur de stuffs automatisé." },
    en: { title: "dofgen - Theorycraft the optimal build", description: "Find the best combination of items according to your criteria with our automated build creator." },
    es: { title: "dofgen - Theorycraft la build óptima", description: "Encuentra la mejor combinación de objetos según tus criterios con nuestro creador de builds automatizado." },
    de: { title: "dofgen - Theorycraft den optimalen Build", description: "Finde mit unserem automatisierten Build-Generator die beste Gegenstandskombination für deine Kriterien." },
    pt: { title: "dofgen - Theorycraft a build ideal", description: "Encontre a melhor combinação de itens segundo os seus critérios com o nosso criador de builds automatizado." },
  },
  encyclopedia: {
    fr: { title: "Encyclopédie – dofgen", description: "Recherche les items et panoplies Dofus par niveau, type et stats." },
    en: { title: "Encyclopedia – dofgen", description: "Search Dofus items and sets by level, type and stats." },
    es: { title: "Enciclopedia – dofgen", description: "Busca los objetos y sets de Dofus por nivel, tipo y stats." },
    de: { title: "Enzyklopädie – dofgen", description: "Durchsuche Dofus-Gegenstände und -Sets nach Stufe, Typ und Stats." },
    pt: { title: "Enciclopédia – dofgen", description: "Pesquise os itens e conjuntos de Dofus por nível, tipo e stats." },
  },
  gen: {
    fr: { title: "Générateur de stuffs – dofgen", description: "Génère le stuff optimal pour ta classe : choisis ton niveau et tes critères, le solveur explore les combinaisons d'items et panoplies et retourne le meilleur stuff." },
    en: { title: "Build generator – dofgen", description: "Generate the optimal build for your class: pick your level and criteria, the solver explores item and set combinations and returns the best build it finds." },
    es: { title: "Generador de builds – dofgen", description: "Genera la build óptima para tu clase: elige tu nivel y tus criterios, el solver explora las combinaciones de objetos y sets y devuelve la mejor build encontrada." },
    de: { title: "Build-Generator – dofgen", description: "Generiere den optimalen Build für deine Klasse: Wähle Stufe und Kriterien, der Solver durchsucht Gegenstands- und Set-Kombinationen und liefert den besten gefundenen Build." },
    pt: { title: "Gerador de builds – dofgen", description: "Gere a build ideal para a sua classe: escolha o seu nível e os seus critérios, o solver explora as combinações de itens e conjuntos e retorna a melhor build encontrada." },
  },
  beta: {
    fr: { title: "Beta – dofgen", description: "Fonctionnalités à venir." },
    en: { title: "Beta – dofgen", description: "Coming soon." },
    es: { title: "Beta – dofgen", description: "Funcionalidades por venir." },
    de: { title: "Beta – dofgen", description: "Demnächst verfügbar." },
    pt: { title: "Beta – dofgen", description: "Funcionalidades em breve." },
  },
  'my-builds': {
    fr: { title: "Mes stuffs – dofgen", description: "Vos stuffs enregistrés et likés, synchronisés via votre compte Discord." },
    en: { title: "My builds – dofgen", description: "Your saved and liked builds, synced via your Discord account." },
    es: { title: "Mis builds – dofgen", description: "Tus builds guardadas y con like, sincronizadas con tu cuenta de Discord." },
    de: { title: "Meine Builds – dofgen", description: "Deine gespeicherten und gelikten Builds, synchronisiert über dein Discord-Konto." },
    pt: { title: "Minhas builds – dofgen", description: "Suas builds salvas e curtidas, sincronizadas pela sua conta Discord." },
  },
  community: {
    fr: { title: "Communauté – dofgen", description: "Découvre les stuffs partagés par les joueurs et les créateurs les plus actifs. Trie par likes, vues ou date et reprends un stuff comme point de départ." },
    en: { title: "Community – dofgen", description: "Discover builds shared by players and the most active creators. Sort by likes, views or date and pick up a build as your starting point." },
    es: { title: "Comunidad – dofgen", description: "Descubre las builds compartidas por los jugadores y los creadores más activos. Ordena por likes, visitas o fecha y retoma una build como punto de partida." },
    de: { title: "Community – dofgen", description: "Entdecke von Spielern geteilte Builds und die aktivsten Ersteller. Sortiere nach Likes, Aufrufen oder Datum und übernimm einen Build als Ausgangspunkt." },
    pt: { title: "Comunidade – dofgen", description: "Descubra as builds compartilhadas pelos jogadores e os criadores mais ativos. Ordene por curtidas, visualizações ou data e use uma build como ponto de partida." },
  },
  terms: {
    fr: { title: "Conditions d'utilisation – dofgen", description: "Propriété du site et de son code, contenu Dofus appartenant à Ankama, et ce qui est autorisé ou non avec dofgen." },
    en: { title: "Terms of use – dofgen", description: "Ownership of the site and its code, Dofus content belonging to Ankama, and what is and isn't allowed with dofgen." },
    es: { title: "Condiciones de uso – dofgen", description: "Propiedad del sitio y de su código, contenido de Dofus perteneciente a Ankama, y lo que está permitido o no con dofgen." },
    de: { title: "Nutzungsbedingungen – dofgen", description: "Eigentum an der Website und ihrem Code, Dofus-Inhalte von Ankama, und was mit dofgen erlaubt ist und was nicht." },
    pt: { title: "Condições de uso – dofgen", description: "Propriedade do site e do seu código, conteúdo Dofus pertencente à Ankama, e o que é ou não permitido com o dofgen." },
  },
  'community/creators': {
    fr: { title: "Créateurs – dofgen", description: "Parcours les créateurs de stuffs dofgen et découvre leurs builds publiés." },
    en: { title: "Creators – dofgen", description: "Browse dofgen build creators and discover their published builds." },
    es: { title: "Creadores – dofgen", description: "Explora los creadores de builds de dofgen y descubre sus builds publicadas." },
    de: { title: "Ersteller – dofgen", description: "Durchstöbere die Build-Ersteller von dofgen und entdecke ihre veröffentlichten Builds." },
    pt: { title: "Criadores – dofgen", description: "Explore os criadores de builds do dofgen e descubra as builds publicadas por eles." },
  },
};

/** Name slot for a build that hasn't been named yet (fresh generator page). */
const TXT_UNNAMED_BUILD = { fr: 'Générateur', en: 'Generator', es: 'Generador', de: 'Generator', pt: 'Gerador' };

/** Title for a build: "Panoplie Kolosso 200 | dofgen" — the build's own name and
 *  level rather than the page's. Used both by server.js for a shared build (tab,
 *  og:title, Twitter card) and by usePageSeo for the build being edited, so a
 *  first load and a client-side navigation to the same build agree. */
function getBuildTitle(lang, name, lvl) {
  const label = (name && String(name).trim()) || TXT_UNNAMED_BUILD[lang] || TXT_UNNAMED_BUILD.en;
  const level = Number(lvl);
  const levelPart = Number.isFinite(level) && level > 0 ? ` ${level}` : '';
  return `${label}${levelPart} | dofgen`;
}

/** Normalized path rest for lookup: /encyclopedia -> encyclopedia, / -> '' */
function getPageKey(pathRest) {
  const p = (pathRest || '/').replace(/^\//, '').trim();
  return p || '';
}

/** First path segment, e.g. /community/luje -> community, /gen/a/b -> gen */
function getFirstSegment(pathRest) {
  return getPageKey(pathRest).split('/')[0];
}

function getLangFromPath(path) {
  const segment = path.replace(/^\//, '').split('/')[0];
  return SUPPORTED_LANGS.includes(segment) ? segment : DEFAULT_LANG;
}

/** Path after the language segment (e.g. /en/encyclopedia -> /encyclopedia, /en -> /) */
function getPathRest(path, lang) {
  return path.replace(new RegExp(`^/${lang}/?`), '/') || '/';
}

/** Merged SEO strings for a page: base STRINGS[lang] + PAGE_STRINGS[pageKey][lang] overrides.
 *  Falls back from an exact path-rest key (e.g. 'community/creators') to the first
 *  segment ('community') so dynamic subpaths (/gen/:slug, /community/:slug) get a
 *  relevant title instead of inheriting the generic homepage one. */
function getStringsForPage(lang, pathRest) {
  const base = STRINGS[lang] || STRINGS[DEFAULT_LANG];
  const page = PAGE_STRINGS[getPageKey(pathRest)] || PAGE_STRINGS[getFirstSegment(pathRest)];
  if (!page || !page[lang]) return base;
  const override = page[lang];
  return {
    ...base,
    title: override.title != null ? override.title : base.title,
    description: override.description != null ? override.description : base.description,
  };
}

function isNoindexPath(pathRest) {
  return NOINDEX_PAGE_KEYS.includes(getFirstSegment(pathRest));
}

/** True when server.js injects per-page meta for this path rest (build/creator). */
function hasServerPageMeta(pathRest) {
  return BUILD_PATH_RE.test(pathRest) || CREATOR_PATH_RE.test(pathRest);
}

module.exports = {
  DEFAULT_LANG,
  SUPPORTED_LANGS,
  STRINGS,
  NOINDEX_PAGE_KEYS,
  BUILD_PATH_RE,
  CREATOR_PATH_RE,
  PAGE_STRINGS,
  getBuildTitle,
  getPageKey,
  getFirstSegment,
  getLangFromPath,
  getPathRest,
  getStringsForPage,
  isNoindexPath,
  hasServerPageMeta,
};
